import type { ArtworkPlacement } from './types';

const API_URL =
  import.meta.env.VITE_ARTCHAIS_API_URL ||
  (window.location.hostname === 'localhost' ? '' : 'https://api.artchais.com');

const EDITOR_USERNAME = import.meta.env.VITE_GALLERY_EDITOR_USERNAME || 'admin';
const LAYOUT_URL = import.meta.env.VITE_GALLERY_LAYOUT_URL || '/data/gallery-layout.json';
const LAYOUT_SAVE_URL = import.meta.env.VITE_GALLERY_LAYOUT_SAVE_URL || `${API_URL}/api/gallery/layout`;

interface GalleryLayoutResponse {
  placements?: ArtworkPlacement[];
}

interface AdminLoginResponse {
  token?: string;
  message?: string;
}

export async function loadGalleryLayout(): Promise<ArtworkPlacement[]> {
  try {
    const response = await fetch(LAYOUT_URL, { cache: 'no-store' });
    if (response.status === 404) return [];
    if (!response.ok) throw new Error(`Layout request failed: ${response.status}`);
    const json = (await response.json()) as GalleryLayoutResponse | ArtworkPlacement[];
    return Array.isArray(json) ? json : json.placements || [];
  } catch (error) {
    console.warn('[gallery] Layout unavailable, using draft or generated placement.', error);
    return [];
  }
}

export async function authenticateGalleryEditor(password: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/admin/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username: EDITOR_USERNAME, password }),
  });
  const json = (await response.json().catch(() => ({}))) as AdminLoginResponse;
  if (!response.ok || !json.token) {
    throw new Error(json.message || 'Password check failed.');
  }
  return json.token;
}

export async function saveGalleryLayout(placements: ArtworkPlacement[], token: string): Promise<void> {
  const response = await fetch(LAYOUT_SAVE_URL, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ placements }),
  });
  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(json.message || `Layout save failed: ${response.status}`);
  }
}
